import { ComponentOptions } from 'vue/types/umd';
import { throttle, ThrottleCallback, DebounceCallback, debounce } from '@owc-utils/Events';
import {
  DebouncedEventListenerOptions,
  EventListenerOptions,
  ThrottledEventListenerOptions,
} from './EventListenerOptions';
import { messages } from './EventListenerMixin.config';

const getComponentsCallbackFn = (method: string, context: any): Function | never => {
  if (!(method in context)) {
    throw new Error(messages.callbackNotFound.replace(/<method>/g, method));
  }
  return context[method];
};

const isThrottledListener = (options: EventListenerOptions): options is ThrottledEventListenerOptions => {
  return options.throttleLimit !== undefined;
};

const isDebouncedListener = (options: EventListenerOptions): options is DebouncedEventListenerOptions => {
  return options.debounceTime !== undefined;
};

/**
 * This function will check first if the method exists in the component and the
 * it will generate a throttled callback for it. If the function doesn't exists
 * in the component then an exception will be thrown
 *
 * @param componentContext `this` context of a vue component
 * @param listenerName methods name within the methods object in the vue component
 * @param options options to configure the throttling
 */
const createThrottledListener = (
  componentContext: any,
  listenerName: string,
  options: ThrottledEventListenerOptions,
): EventListener => {
  const callback = getComponentsCallbackFn(listenerName, componentContext) as ThrottleCallback;
  return throttle(options.throttleLimit, callback);
};

/**
 * This function will check first if the method exists in the component and the
 * it will generate a throttled callback for it. If the function doesn't exists
 * in the component then an exception will be thrown
 *
 * @param componentContext `this` context of a vue component
 * @param listenerName methods name within the methods object in the vue component
 * @param options options to configure the debouncing
 */
const createDebouncedListener = (
  componentContext: any,
  listenerName: string,
  options: DebouncedEventListenerOptions,
): EventListener => {
  const callback = getComponentsCallbackFn(listenerName, componentContext) as DebounceCallback;
  return debounce(options.debounceTime, callback, options.runImmediately || false);
};

const createDefaultEventListener = (componentContext: any, listenerName: string): EventListener | never => {
  return getComponentsCallbackFn(listenerName, componentContext) as EventListener;
};

/**
 * This is a mixin factory that allows you to create a mixin for registering a
 * global event listener and managing it's lifecicle. This should avoid reinventing
 * the wheel when registering global event listeners.
 *
 * @param eventType anything what addEventListener can take as first argument
 * @param listenerName method name in to be invoked
 * @param options options to configure behavior of addEventListener and throttling
 */
export function createEventListener<T extends keyof WindowEventMap>(
  eventType: T,
  listenerName: string,
  options: EventListenerOptions,
): ComponentOptions<Vue> {
  let eventListener: EventListener;

  // this helper function will create an event listener depending on
  // the keys passed as options. It will create a throttled listener
  // if `throttleLimit` is set or a debounced listener if `debounceTime`
  // is set. If not a regular event listener will be created
  const createListener = (componentContext: any): EventListener => {
    if (isThrottledListener(options)) {
      return createThrottledListener(componentContext, listenerName, options);
    } else if (isDebouncedListener(options)) {
      return createDebouncedListener(componentContext, listenerName, options);
    } else {
      return createDefaultEventListener(componentContext, listenerName);
    }
  };

  return {
    mounted() {
      // this is the component's this which the mixin is added to
      eventListener = createListener(this);

      window.addEventListener(eventType, eventListener, options);
    },
    beforeDestroy() {
      window.removeEventListener(eventType, eventListener);
    },
  };
}
